import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Camera, User, Loader2, LogOut } from "lucide-react";

const AdminProfileDialog = ({ open, onOpenChange, user, headers, logout, adminAvatar, setAdminAvatar }) => {
  const [savingAvatar, setSavingAvatar] = useState(false);

  const handleAdminAvatarUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error("Image must be less than 2MB");
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setAdminAvatar(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const saveAdminProfile = async () => {
    setSavingAvatar(true);
    try {
      await axios.put(`${API}/admin/profile`, { avatar: adminAvatar }, { headers });
      toast.success("Profile updated!");
      onOpenChange(false);
    } catch (error) {
      toast.error("Failed to update profile");
    } finally {
      setSavingAvatar(false);
    }
  };

  return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-[#3D4035]">Profile Settings</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 pt-4">
            {/* Avatar Upload */}
            <div className="flex flex-col items-center">
              <div className="relative">
                <div className="w-28 h-28 rounded-full overflow-hidden bg-[#F4F1DE] border-4 border-white shadow-lg">
                  {adminAvatar ? (
                    <img 
                      src={adminAvatar} 
                      alt="Admin Profile" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <User className="w-12 h-12 text-[#3D4035]/30" />
                    </div>
                  )}
                </div>
                <label 
                  htmlFor="admin-avatar-upload" 
                  className="absolute bottom-0 right-0 w-10 h-10 bg-[#E07A5F] rounded-full flex items-center justify-center cursor-pointer shadow-lg hover:bg-[#D06A4F] transition-colors"
                >
                  <Camera className="w-5 h-5 text-white" />
                  <input
                    id="admin-avatar-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleAdminAvatarUpload}
                    className="hidden"
                    data-testid="admin-avatar-upload-input"
                  />
                </label>
              </div>
              <p className="text-center text-[#3D4035]/50 text-sm mt-3">
                Click to update your profile photo
              </p>
            </div>

            {/* Admin Info */}
            <div className="bg-[#F4F1DE] rounded-xl p-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-[#3D4035]/60 text-sm">Name</span>
                <span className="text-[#3D4035] font-medium">{user?.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#3D4035]/60 text-sm">Email</span>
                <span className="text-[#3D4035] font-medium">{user?.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#3D4035]/60 text-sm">Role</span>
                <span className="text-[#E07A5F] font-medium capitalize">{user?.role}</span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-[#E07A5F] hover:bg-[#D06A4F]"
                onClick={saveAdminProfile}
                disabled={savingAvatar}
              >
                {savingAvatar ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  "Save Changes"
                )}
              </Button>
            </div>

            {/* Logout */}
            <Button
              variant="ghost"
              className="w-full text-red-500 hover:text-red-600 hover:bg-red-50"
              onClick={logout}
            >
              <LogOut className="w-4 h-4 mr-2" />
              Log Out
            </Button>
          </div>
        </DialogContent>
      </Dialog>
  );
};

export default AdminProfileDialog;
